import { useState, useEffect, useCallback } from 'react';
import { facturesAPI } from '../services/api';

interface ClientSummary {
  total_payee: number;
  total_impayee: number;
  total_avoir: number;
  plafond_fixe: number;
  plafond_depasse: number;
  chiffre_affaires: number;
}

interface UseClientPlafondReturn {
  clientSummary: ClientSummary | null;
  isPlafondDepasse: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to check if a client has exceeded their credit limit (plafond)
 * @param clientId - The client ID to check, or null/undefined if no client selected
 * @returns Object containing summary data, whether plafond is exceeded, loading state, and refetch function
 */
export const useClientPlafond = (clientId: number | null | undefined): UseClientPlafondReturn => {
  const [clientSummary, setClientSummary] = useState<ClientSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClientSummary = useCallback(async () => {
    if (!clientId) {
      setClientSummary(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const summary = await facturesAPI.getClientSummary(clientId);
      setClientSummary(summary);
    } catch (err) {
      console.error('Error fetching client summary:', err);
      setError('Erreur lors du chargement des informations du client');
      setClientSummary(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientSummary();
  }, [fetchClientSummary]);

  // Block if: plafond exceeded OR plafond is 0 (no credit allowed)
  const isPlafondDepasse = clientSummary !== null &&
    (clientSummary.plafond_depasse > 0 || clientSummary.plafond_fixe === 0);

  return {
    clientSummary,
    isPlafondDepasse,
    loading,
    error,
    refetch: fetchClientSummary,
  };
};

export default useClientPlafond;
